import { useEffect, useMemo, useState } from "react";
import { BarChart3, ClipboardList, PackageCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { getError } from "@/api";
import { PdfFileActions } from "@/components/PdfFileActions";

const periods = [
  ["all", "Toute la période"],
  ["today", "Aujourd’hui"],
  ["yesterday", "Hier"],
  ["this_week", "Cette semaine"],
  ["last_7_days", "Les 7 derniers jours"],
  ["this_month", "Ce mois-ci"],
  ["last_30_days", "Les 30 derniers jours"],
  ["last_90_days", "Les 90 derniers jours"],
  ["this_year", "Cette année"],
  ["custom", "Dates personnalisées"],
];

const warehouseTypes = [
  ["principal", "Dépôt principal"],
  ["zone_conakry", "Zone Conakry"],
  ["zone_interieur", "Zone intérieur"],
  ["livreur", "Dépôt livreur"],
];

const compactParams = (filters) => Object.fromEntries(
  Object.entries(filters).filter(([, value]) => value),
);

export default function StockReportsPage() {
  const [zones, setZones] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [stockState, setStockState] = useState(null);
  const [history, setHistory] = useState(null);
  const [isLoadingStock, setIsLoadingStock] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [stockFilters, setStockFilters] = useState({
    warehouse_id: "",
    zone_id: "",
    subzone: "",
    warehouse_type: "",
  });
  const [historyFilters, setHistoryFilters] = useState({
    product_id: "",
    warehouse_id: "",
    zone_id: "",
    subzone: "",
    warehouse_type: "",
    period: "all",
    date_from: "",
    date_to: "",
  });

  const availableZoneIds = useMemo(
    () => new Set(warehouses.map((warehouse) => warehouse.zone_id)),
    [warehouses],
  );
  const availableZones = zones.filter((zone) => availableZoneIds.has(zone.zone_id));
  const selectedStockZone = zones.find((zone) => zone.zone_id === stockFilters.zone_id);
  const selectedHistoryZone = zones.find((zone) => zone.zone_id === historyFilters.zone_id);
  const stockWarehouses = warehouses.filter((warehouse) => (
    (!stockFilters.zone_id || warehouse.zone_id === stockFilters.zone_id)
    && (!stockFilters.subzone || warehouse.subzone === stockFilters.subzone)
    && (!stockFilters.warehouse_type || warehouse.warehouse_type === stockFilters.warehouse_type)
  ));
  const historyWarehouses = warehouses.filter((warehouse) => (
    (!historyFilters.zone_id || warehouse.zone_id === historyFilters.zone_id)
    && (!historyFilters.subzone || warehouse.subzone === historyFilters.subzone)
    && (!historyFilters.warehouse_type || warehouse.warehouse_type === historyFilters.warehouse_type)
  ));

  const loadContext = async () => {
    try {
      const [warehouseResponse, zoneResponse, productResponse] = await Promise.all([
        api.get("/reports/stock-context"),
        api.get("/zones"),
        api.get("/products"),
      ]);
      setWarehouses(warehouseResponse.data);
      setZones(zoneResponse.data);
      setProducts(productResponse.data.filter((product) => product.active));
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const loadStockState = async () => {
    setIsLoadingStock(true);
    try {
      const response = await api.get("/reports/stock-state", {
        params: compactParams(stockFilters),
      });
      setStockState(response.data);
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setIsLoadingStock(false);
    }
  };

  useEffect(() => {
    loadContext();
  }, []);

  useEffect(() => {
    loadStockState();
  }, [
    stockFilters.warehouse_id,
    stockFilters.zone_id,
    stockFilters.subzone,
    stockFilters.warehouse_type,
  ]);

  const updateStockFilter = (key, value) => {
    setStockFilters((current) => ({ ...current, [key]: value }));
  };

  const updateStockLocation = (key, value) => {
    setStockFilters((current) => ({
      ...current,
      [key]: value,
      warehouse_id: key === "warehouse_id" ? value : "",
      ...(key === "zone_id" ? { subzone: "" } : {}),
    }));
  };

  const updateHistoryFilter = (key, value) => {
    setHistory(null);
    setHistoryFilters((current) => ({ ...current, [key]: value }));
  };

  const updateHistoryLocation = (key, value) => {
    setHistory(null);
    setHistoryFilters((current) => ({
      ...current,
      [key]: value,
      warehouse_id: key === "warehouse_id" ? value : "",
      ...(key === "zone_id" ? { subzone: "" } : {}),
    }));
  };

  const loadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await api.get("/reports/stock-history", {
        params: compactParams(historyFilters),
      });
      setHistory(response.data);
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const getStockPdfBlob = async () => {
    const response = await api.get("/reports/stock-state/pdf", {
      params: compactParams(stockFilters),
      responseType: "blob",
    });
    return response.data;
  };

  const getHistoryPdfBlob = async () => {
    const response = await api.get("/reports/stock-history/pdf", {
      params: compactParams(historyFilters),
      responseType: "blob",
    });
    return response.data;
  };

  return (
    <div className="page-enter stock-reports-page" data-testid="stock-reports-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">ÉTATS ET TRAÇABILITÉ</p>
          <h1 data-testid="stock-reports-title">Rapports de stock</h1>
          <p data-testid="stock-reports-description">
            Consultez votre périmètre autorisé, puis éditez des PDF A4 prêts à partager.
          </p>
        </div>
      </div>

      <section className="data-panel stock-report-filter-panel" data-testid="stock-state-report-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">INVENTAIRE</p>
            <h2>État général du stock</h2>
          </div>
          <PackageCheck aria-hidden="true" size={22} />
        </div>
        <div className="stock-report-filter-grid">
          <label>
            <span>Zone</span>
            <select
              data-testid="stock-report-zone-select"
              onChange={(event) => updateStockLocation("zone_id", event.target.value)}
              value={stockFilters.zone_id}
            >
              <option value="">Tout mon périmètre</option>
              {availableZones.map((zone) => (
                <option key={zone.zone_id} value={zone.zone_id}>{zone.name}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Sous-zone</span>
            <select
              data-testid="stock-report-subzone-select"
              disabled={!stockFilters.zone_id}
              onChange={(event) => updateStockLocation("subzone", event.target.value)}
              value={stockFilters.subzone}
            >
              <option value="">Toutes les sous-zones</option>
              {selectedStockZone?.subzones.map((subzone) => (
                <option key={subzone} value={subzone}>{subzone}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Type de dépôt</span>
            <select
              data-testid="stock-report-warehouse-type-select"
              onChange={(event) => updateStockLocation("warehouse_type", event.target.value)}
              value={stockFilters.warehouse_type}
            >
              <option value="">Tous les types</option>
              {warehouseTypes.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Dépôt</span>
            <select
              data-testid="stock-report-warehouse-select"
              onChange={(event) => updateStockLocation("warehouse_id", event.target.value)}
              value={stockFilters.warehouse_id}
            >
              <option value="">Tous les dépôts accessibles</option>
              {stockWarehouses.map((warehouse) => (
                <option key={warehouse.warehouse_id} value={warehouse.warehouse_id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          </label>
          <button
            className="secondary-action"
            data-testid="refresh-stock-state-button"
            onClick={loadStockState}
            type="button"
          >
            <RefreshCw size={16} />
            Actualiser
          </button>
        </div>
        <div className="report-pdf-actions stock-report-actions" data-testid="stock-state-pdf-actions">
          <span>État A4 avec quantités, valorisation et seuils.</span>
          <PdfFileActions
            fileName="etat-general-stock.pdf"
            getPdfBlob={getStockPdfBlob}
            testIdPrefix="stock-state-report"
          />
        </div>
        <StockStatePreview data={stockState} loading={isLoadingStock} />
      </section>

      <section className="data-panel stock-report-filter-panel" data-testid="stock-history-report-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">MOUVEMENTS PAR PRODUIT</p>
            <h2>Historique d’un ou de tous les produits</h2>
          </div>
          <ClipboardList aria-hidden="true" size={22} />
        </div>
        <div className="stock-report-filter-grid">
          <label>
            <span>Produit</span>
            <select
              data-testid="stock-history-product-select"
              onChange={(event) => updateHistoryFilter("product_id", event.target.value)}
              value={historyFilters.product_id}
            >
              <option value="">Tous les produits</option>
              {products.map((product) => (
                <option key={product.product_id} value={product.product_id}>{product.name}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Zone</span>
            <select
              data-testid="stock-history-zone-select"
              onChange={(event) => updateHistoryLocation("zone_id", event.target.value)}
              value={historyFilters.zone_id}
            >
              <option value="">Tout mon périmètre</option>
              {availableZones.map((zone) => (
                <option key={zone.zone_id} value={zone.zone_id}>{zone.name}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Sous-zone</span>
            <select
              data-testid="stock-history-subzone-select"
              disabled={!historyFilters.zone_id}
              onChange={(event) => updateHistoryLocation("subzone", event.target.value)}
              value={historyFilters.subzone}
            >
              <option value="">Toutes les sous-zones</option>
              {selectedHistoryZone?.subzones.map((subzone) => (
                <option key={subzone} value={subzone}>{subzone}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Type de dépôt</span>
            <select
              data-testid="stock-history-warehouse-type-select"
              onChange={(event) => updateHistoryLocation("warehouse_type", event.target.value)}
              value={historyFilters.warehouse_type}
            >
              <option value="">Tous les types</option>
              {warehouseTypes.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Dépôt</span>
            <select
              data-testid="stock-history-warehouse-select"
              onChange={(event) => updateHistoryLocation("warehouse_id", event.target.value)}
              value={historyFilters.warehouse_id}
            >
              <option value="">Tous les dépôts accessibles</option>
              {historyWarehouses.map((warehouse) => (
                <option key={warehouse.warehouse_id} value={warehouse.warehouse_id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Période</span>
            <select
              data-testid="stock-history-period-select"
              onChange={(event) => updateHistoryFilter("period", event.target.value)}
              value={historyFilters.period}
            >
              {periods.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          {historyFilters.period === "custom" && (
            <>
              <label>
                <span>Du</span>
                <input
                  data-testid="stock-history-date-from-input"
                  onChange={(event) => updateHistoryFilter("date_from", event.target.value)}
                  type="date"
                  value={historyFilters.date_from}
                />
              </label>
              <label>
                <span>Au</span>
                <input
                  data-testid="stock-history-date-to-input"
                  onChange={(event) => updateHistoryFilter("date_to", event.target.value)}
                  type="date"
                  value={historyFilters.date_to}
                />
              </label>
            </>
          )}
          <button
            className="primary-action"
            data-testid="load-stock-history-button"
            disabled={isLoadingHistory}
            onClick={loadHistory}
            type="button"
          >
            <BarChart3 size={16} />
            {isLoadingHistory ? "Chargement…" : "Afficher l’historique"}
          </button>
        </div>
        {history && (
          <div className="stock-history-result" data-testid="stock-history-result">
            <div className="report-pdf-actions stock-report-actions">
              <span data-testid="stock-history-scope-label">
                {history.scope_label} · {history.product_label} · {history.period_label}
              </span>
              <PdfFileActions
                fileName="historique-stock.pdf"
                getPdfBlob={getHistoryPdfBlob}
                testIdPrefix="stock-history-report"
              />
            </div>
            <StockHistoryPreview data={history} />
          </div>
        )}
      </section>
    </div>
  );
}

function StockStatePreview({ data, loading }) {
  if (loading) {
    return <div className="loading-line" data-testid="stock-state-loading">Chargement…</div>;
  }

  if (!data) {
    return null;
  }

  return (
    <div className="report-a4-preview" data-testid="stock-state-preview">
      <div className="report-a4-heading">
        <span>ECOM GUINÉE</span>
        <strong>État général du stock</strong>
        <small>{data.summary.warehouse_count} dépôt(s) · {data.summary.alert_count} alerte(s)</small>
      </div>
      <div className="stock-report-metrics" data-testid="stock-state-summary">
        <span>Produits<strong>{data.summary.product_line_count}</strong></span>
        <span>Quantité<strong>{data.summary.quantity_total}</strong></span>
        <span>Valeur<strong>{data.summary.value_total.toLocaleString("fr-FR")} GNF</strong></span>
      </div>
      <div className="responsive-table">
        <table data-testid="stock-state-preview-table">
          <thead>
            <tr><th>Dépôt</th><th>Produit</th><th>Qté</th><th>Valeur</th><th>État</th></tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={`${row.warehouse_id}-${row.product_id}`}>
                <td>{row.warehouse_name}</td>
                <td>{row.product_name}</td>
                <td>{row.quantity}</td>
                <td>{row.value.toLocaleString("fr-FR")} GNF</td>
                <td>{row.is_alert ? "Sous seuil" : "Normal"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!data.rows.length && <p className="empty-inline">Aucun stock pour ce périmètre.</p>}
    </div>
  );
}

function StockHistoryPreview({ data }) {
  return (
    <div className="report-a4-preview" data-testid="stock-history-preview">
      <div className="report-a4-heading">
        <span>ECOM GUINÉE</span>
        <strong>Historique de stock</strong>
        <small>{data.summary.line_count} ligne(s) · {data.summary.quantity_total} unité(s)</small>
      </div>
      <div className="responsive-table">
        <table data-testid="stock-history-preview-table">
          <thead>
            <tr><th>Date</th><th>Produit</th><th>Opération</th><th>Qté</th><th>Dépôts</th></tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={`${row.movement_id}-${row.product_id}`}>
                <td>{new Date(row.movement_date).toLocaleDateString("fr-FR")}</td>
                <td>{row.product_name}</td>
                <td>{row.movement_type}</td>
                <td>{row.quantity}</td>
                <td>{row.source_name} → {row.destination_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!data.rows.length && <p className="empty-inline">Aucun mouvement pour cette période.</p>}
    </div>
  );
}